import React, { useEffect, useRef } from "react";
import * as c from "./lpCompetitorQuote.module.scss";
import classNames from "classnames";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  LpCompetitorQuoteGlow,
  LpCompetitorReviewBadges,
  competitorData,
} from "@components/shared/landingPage/competitor";

gsap.registerPlugin(ScrollTrigger);

const LpCompetitorQuote = ({ slug }) => {
  const cardRefs = useRef([]);
  const data = competitorData[slug];

  const displayQuotes = (data?.quotes || []).map((q, i) => ({
    id: i + 1,
    text: q.quote,
    author: q.author,
    authorLink: q.authorLink,
    role: q.authorDescription,
  }));

  useEffect(() => {
    if (!data) return;

    const triggers = [];

    // Only create scroll triggers for cards that have a next card (skip the last card)
    for (let index = 0; index < cardRefs.current.length - 1; index++) {
      const card = cardRefs.current[index];
      if (!card) continue;

      const cardContent = card.querySelector(`.${c.cardContent}`);
      const textElements = card.querySelectorAll(`.${c.quoteIcon}, .${c.quoteText}, .${c.author}`);
      const nextCard = cardRefs.current[index + 1];

      if (!cardContent || !nextCard) continue;

      const trigger = ScrollTrigger.create({
        trigger: card,
        start: "top bottom",
        end: "bottom top",
        onUpdate: () => {
          const rect = card.getBoundingClientRect();
          const nextRect = nextCard.getBoundingClientRect();
          const distance = nextRect.top - (rect.top + 40);
          const overlap = distance <= 300 ? Math.max(0, (300 - distance) / 300) : 0;

          gsap.set(cardContent, {
            scale: 1 - overlap * 0.05,
            opacity: 1 - overlap * 0.35,
            transformOrigin: "top center",
          });

          textElements.forEach((el) => gsap.set(el, { opacity: 1 - overlap }));
        },
      });

      triggers.push(trigger);
    }

    return () => {
      triggers.forEach((st) => st?.kill());
    };
  }, [data]);

  if (!data) return null;

  return (
    <>
      <LpCompetitorReviewBadges />
      <ul className={c.cards}>
        {displayQuotes.map((quote, index) => (
          <li key={quote.id} className={c.card} ref={(el) => (cardRefs.current[index] = el)}>
            {index === 0 && <LpCompetitorQuoteGlow className={c.glowSvg} />}
            <div className={c.cardContent}>
              <div className={c.quoteIcon}>
                <svg width="30" height="24" viewBox="0 0 30 24">
                  <path
                    d="M6.38298 24C4.42553 24 2.85106 23.4526 1.65957 22.3579C0.553192 21.1789 0 19.621 0 17.6842C0 14.7368 0.510638 12.1263 1.53192 9.8526C2.6383 7.57892 3.82979 5.72629 5.10638 4.29471C6.6383 2.6105 8.34043 1.17892 10.2128 -3.05176e-05L11.4894 1.89471C10.6383 2.73682 9.87234 3.62103 9.19149 4.54734C8.59575 5.38944 8 6.39997 7.40426 7.57892C6.89362 8.67365 6.55319 9.93681 6.38298 11.3684C8.34043 11.3684 9.87234 11.9579 10.9787 13.1368C12.1702 14.2316 12.766 15.7473 12.766 17.6842C12.766 19.621 12.1702 21.1789 10.9787 22.3579C9.87234 23.4526 8.34043 24 6.38298 24ZM23.617 24C21.6596 24 20.0851 23.4526 18.8936 22.3579C17.7872 21.1789 17.234 19.621 17.234 17.6842C17.234 14.7368 17.7447 12.1263 18.766 9.8526C19.8723 7.57892 21.0638 5.72629 22.3404 4.29471C23.8723 2.6105 25.5745 1.17892 27.4468 -3.05176e-05L28.7234 1.89471C27.8723 2.73682 27.1064 3.62103 26.4255 4.54734C25.8298 5.38944 25.234 6.39997 24.6383 7.57892C24.1277 8.67365 23.7872 9.93681 23.617 11.3684C25.5745 11.3684 27.1064 11.9579 28.2128 13.1368C29.4043 14.2316 30 15.7473 30 17.6842C30 19.621 29.4043 21.1789 28.2128 22.3579C27.1064 23.4526 25.5745 24 23.617 24Z"
                    fill="#C098F5"
                  />
                </svg>
              </div>
              <p className={classNames(c.quoteText, "p2")}>{quote.text}</p>
              <div className={c.author}>
                <span className={classNames(c.authorName, "p4")} dangerouslySetInnerHTML={{ __html: quote.author }} />
                {quote.role && <span className="p5">{quote.role}</span>}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
};

export default LpCompetitorQuote;
